import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import DashboardLayout from "../../../layouts/DashboardLayout";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import RouterOutlinedIcon from "@mui/icons-material/RouterOutlined";
import SimCardOutlinedIcon from "@mui/icons-material/SimCardOutlined";
import ExtensionOutlinedIcon from "@mui/icons-material/ExtensionOutlined";
import SatelliteAltOutlinedIcon from "@mui/icons-material/SatelliteAltOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import SettingsInputAntennaOutlinedIcon from "@mui/icons-material/SettingsInputAntennaOutlined";
import Battery5BarOutlinedIcon from "@mui/icons-material/Battery5BarOutlined";
import DevicesOtherOutlinedIcon from "@mui/icons-material/DevicesOtherOutlined";
import HistoryIcon from "@mui/icons-material/History";
import {
  Alert, Box, Button, Chip, CircularProgress,
  Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, IconButton, MenuItem, Paper, Stack,
  Switch, Tab, Tabs, TextField, Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { alpha } from "@mui/material/styles";
import clienteServicioService from "../services/clienteServicioService";
import HistorialResultsTable from "../components/HistorialResultsTable";
import ResumenEquipo from "../components/ResumenEquipo";
import MantenimientosPorReq from "../components/MantenimientosPorReq";
import BusquedaPorIdentificador from "../../centroEquipos/components/BusquedaPorIdentificador";
import ClientePlataformaVehiculos from "../../panelGlobal/components/ClientePlataformaVehiculos";
import OrdenTrabajoDetalle from "../../ordenDeTrabajo/pages/OrdenTrabajoDetalle";
import { getPagoLabel } from "../../clientes/constants/pago";

// ─────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────
function formatDateDDMMYYYY(dateLike) {
  if (!dateLike) return "—";
  const d = new Date(dateLike);
  if (Number.isNaN(d.getTime())) return "—";
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
}

function formatDateTimeDDMMYYYY(dateLike) {
  if (!dateLike) return "—";
  const d = new Date(dateLike);
  if (Number.isNaN(d.getTime())) return "—";
  const fecha = `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
  const hora = `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  return `${fecha} ${hora}`;
}

const PLATAFORMA_ACTUALIZACION_LABEL = {
  flespi1: "Flespi 1",
  flespi2: "Flespi 2",
  wialon: "Wialon",
  leasecontrol: "Leasecontrol",
};

// Al hacer click en una línea de "Última Actualización", consulta la posición actual
// del módem en ESA plataforma puntual (no la tabla modem_ultima_conexion, que solo
// guarda la fecha) y abre Google Maps ahí. La pestaña se abre en blanco ya, en el mismo
// gesto de click, para que el navegador no la bloquee como popup (mismo patrón que los
// PDF de Nota de Entrega/Devolución).
const abrirCoordenadasModem = async (row, plataforma) => {
  const mapsWindow = window.open("", "_blank");
  if (mapsWindow) {
    mapsWindow.opener = null;
    mapsWindow.document.title = "Buscando ubicación...";
    mapsWindow.document.body.innerHTML =
      '<div style="font-family:Arial,sans-serif;padding:24px;color:#334155">Buscando la última posición reportada...</div>';
  }
  try {
    const data = await clienteServicioService.getCoordenadasModem({
      num_sim: row?.num_sim,
      emai: row?.emai,
      plataforma,
    });
    if (data?.lat == null || data?.lng == null) throw new Error("Sin coordenadas");
    const url = `https://www.google.com/maps?q=${data.lat},${data.lng}`;
    if (mapsWindow) mapsWindow.location.replace(url);
    else window.open(url, "_blank");
  } catch (err) {
    if (mapsWindow) mapsWindow.close();
    alert(err?.response?.data?.error || "No se pudo obtener la ubicación actual de este equipo en esa plataforma.");
  }
};

const MONEDAS = { 1: "Dólares", 2: "Bolivianos" };

function chipsToText(chips) {
  if (!Array.isArray(chips) || chips.length === 0) return "—";
  return chips.map((c) => `${c.numero}${c.estado ? ` (${c.estado})` : ""}`).join(", ");
}

const SENSOR_KEYS = [
  ["acelerometro", "Acelerometro"],
  ["temperatura", "Temperatura"],
  ["ibutton", "IButton"],
  ["buzer", "Buzer"],
  ["corte_motor", "Corte motor"],
  ["boton_panico", "Boton panico"],
  ["sensor_puerta", "Sensor puerta"],
];

const DEFAULT_SENSORES = Object.fromEntries(SENSOR_KEYS.map(([key]) => [key, false]));

function parseSensores(value) {
  let raw = value;
  if (typeof raw === "string") {
    try {
      raw = JSON.parse(raw);
    } catch {
      raw = {};
    }
  }
  const source = raw && typeof raw === "object" ? raw : {};
  return Object.fromEntries(SENSOR_KEYS.map(([key]) => [key, source[key] === true]));
}

function getNumSimFromRow(row) {
  return row?.num_sim || row?.serie || "";
}

const editFieldSx = {
  "& .MuiInputBase-root": {
    minHeight: 32,
    fontSize: "0.78rem",
  },
  "& .MuiInputBase-input": {
    py: "5px",
  },
  "& .MuiSelect-select": {
    py: "5px",
  },
  "& .MuiInputLabel-root": {
    fontSize: "0.76rem",
  },
};

const editDialogTitleSx = {
  py: 0.75,
  fontSize: "0.9rem",
  fontWeight: 900,
  lineHeight: 1.15,
};

function ModemDatosAvlDialog({ open, row, loading, error, onClose, onSave }) {
  const [form, setForm] = useState({
    id_ref: "",
    sociedad: "",
    transportista: "",
    grupo: "",
    bandera: "no",
    sensores: DEFAULT_SENSORES,
  });

  useEffect(() => {
    if (!open || !row) return;
    const bandera = String(row?.bandera || "").trim().toLowerCase();
    setForm({
      id_ref: row?.id_ref || "",
      sociedad: row?.sociedad || "",
      transportista: row?.transportista || "",
      grupo: row?.grupo || "",
      bandera: bandera === "si" || bandera === "bandera" || bandera === "true" || bandera === "1" ? "si" : "no",
      sensores: parseSensores(row?.sensores),
    });
  }, [open, row]);

  const handleField = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleSensor = (key) => (event) => {
    setForm((prev) => ({
      ...prev,
      sensores: { ...prev.sensores, [key]: event.target.checked },
    }));
  };

  const submit = () => onSave?.({
    ...form,
    bandera: form.bandera === "si" ? "si" : "",
  });

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 1.25 } }}
    >
      <DialogTitle sx={editDialogTitleSx}>
        Editar modem AVL
        <Typography variant="caption" sx={{ display: "block", color: "text.secondary", fontSize: "0.68rem" }}>
          {getNumSimFromRow(row)} {row?.placa ? `- ${row.placa}` : ""}
        </Typography>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 1 }}>
        {error && <Alert severity="error" sx={{ mb: 0.75, py: 0 }}>{error}</Alert>}

        <Grid container spacing={0.75}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField fullWidth size="small" label="Alias / ID Ref" value={form.id_ref} onChange={handleField("id_ref")} sx={editFieldSx} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField fullWidth size="small" label="Sociedad" value={form.sociedad} onChange={handleField("sociedad")} sx={editFieldSx} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField fullWidth size="small" label="Transportista" value={form.transportista} onChange={handleField("transportista")} sx={editFieldSx} />
          </Grid>
          <Grid size={{ xs: 8, sm: 4 }}>
            <TextField fullWidth size="small" label="Grupo" value={form.grupo} onChange={handleField("grupo")} sx={editFieldSx} />
          </Grid>
          <Grid size={{ xs: 4, sm: 2 }}>
            <TextField select fullWidth size="small" label="Bandera" value={form.bandera} onChange={handleField("bandera")} sx={editFieldSx}>
              <MenuItem value="no">No</MenuItem>
              <MenuItem value="si">Si</MenuItem>
            </TextField>
          </Grid>
        </Grid>

        <Typography variant="caption" sx={{ mt: 0.9, mb: 0.25, display: "block", fontWeight: 900, color: "text.secondary" }}>
          Sensores
        </Typography>
        <Grid container spacing={0.25}>
          {SENSOR_KEYS.map(([key, label]) => (
            <Grid key={key} size={{ xs: 6, sm: 4, md: 3 }}>
              <FormControlLabel
                control={<Switch size="small" checked={!!form.sensores[key]} onChange={handleSensor(key)} />}
                label={<Typography variant="caption" sx={{ fontSize: "0.7rem" }}>{label}</Typography>}
                sx={{ m: 0, height: 26 }}
              />
            </Grid>
          ))}
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: 0.75 }}>
        <Button size="small" onClick={onClose} disabled={loading}>Cancelar</Button>
        <Button size="small" variant="contained" onClick={submit} disabled={loading}>
          {loading ? "Guardando..." : "Guardar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function EquipoDatosDialog({ open, tipo, row, loading, error, onClose, onSave }) {
  const isRadio = tipo === "radio";
  const [form, setForm] = useState({ placa: "", grupo: "", id_ref: "" });

  useEffect(() => {
    if (!open || !row) return;
    setForm({
      placa: row?.placa || "",
      grupo: row?.grupo || "",
      id_ref: row?.id_ref || "",
    });
  }, [open, row]);

  const handleField = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 1.25 } }}
    >
      <DialogTitle sx={editDialogTitleSx}>
        Editar {isRadio ? "radio" : "equipo"}
        <Typography variant="caption" sx={{ display: "block", color: "text.secondary", fontSize: "0.68rem" }}>
          {row?.serie || row?.id2 || ""}
        </Typography>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 1 }}>
        {error && <Alert severity="error" sx={{ mb: 0.75, py: 0 }}>{error}</Alert>}
        <Grid container spacing={0.75}>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField fullWidth size="small" label="Placa" value={form.placa} onChange={handleField("placa")} sx={editFieldSx} />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField fullWidth size="small" label="Grupo" value={form.grupo} onChange={handleField("grupo")} sx={editFieldSx} />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField fullWidth size="small" label="Alias / ID Ref" value={form.id_ref} onChange={handleField("id_ref")} sx={editFieldSx} />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: 0.75 }}>
        <Button size="small" onClick={onClose} disabled={loading}>Cancelar</Button>
        <Button size="small" variant="contained" onClick={() => onSave?.(form)} disabled={loading}>
          {loading ? "Guardando..." : "Guardar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
// Fuera del componente, estático:
const getRowSxByEstado = (row) => {
  if (String(row?.estado).toLowerCase() === "inactivo") {
    return { bgcolor: "#ede7f6" }; // lila suave
  }
  return {};
};

// ─────────────────────────────────────────────────────────────
// COLUMNAS POR ACCIÓN (estáticas, fuera del componente)
// ─────────────────────────────────────────────────────────────
const COLUMNS = {
  "modems-activos": [
    { key: "placa",        header: "Placa" },
    { key: "id",           header: "ID" },
    { key: "num_sim",      header: "Serie" },
    { key: "emai",         header: "IMEI" },
    { key: "modelo_nombre",header: "Modelo" },
    { key: "id_ref",       header: "Alias" },
    { key: "grupo",        header: "Grupo" },
    { key: "es",           header: "Estado" },
    { key: "fecha",        header: "Fecha de Adición", render: (r) => formatDateDDMMYYYY(r?.fecha) },
    { key: "ciudad",       header: "Ciudad" },
    { key: "chips",        header: "Chips", render: (r) => chipsToText(r?.chips) },
    {
      key: "conexiones",
      header: "Última Actualización",
      // Un modem puede estar reportando en mas de una plataforma a la vez
      // (ej. Wialon y Flespi) — se muestran todas, una por linea. Cada linea es
      // clicable: consulta la posicion actual en esa plataforma y abre Google Maps.
      render: (r) => {
        const conexiones = r?.conexiones || [];
        if (!conexiones.length) return "—";
        return (
          <Stack spacing={0.1}>
            {conexiones.map((c) => (
              <Box
                key={c.plataforma}
                component="span"
                onClick={() => abrirCoordenadasModem(r, c.plataforma)}
                sx={{ fontSize: "inherit", whiteSpace: "nowrap", cursor: "pointer", color: "primary.main", textDecoration: "underline" }}
              >
                {formatDateTimeDDMMYYYY(c.fecha_actualizacion)} ({PLATAFORMA_ACTUALIZACION_LABEL[c.plataforma] || c.plataforma})
              </Box>
            ))}
          </Stack>
        );
      },
    },
  ],

  "modems-solo-activos": [
    { key: "placa",   header: "Placa" },
    { key: "id",      header: "ID" },
    { key: "num_sim", header: "Serie" },
    { key: "emai",    header: "IMEI" },
    { key: "modelo",  header: "Modelo" },   // ← diferente al otro endpoint
    { key: "id_ref",  header: "Alias" },
    { key: "grupo",   header: "Grupo" },
    { key: "es",      header: "Estado" },
    { key: "fecha",   header: "Fecha de Adición", render: (r) => formatDateDDMMYYYY(r?.fecha) },
    { key: "ciudad",  header: "Ciudad" },
    { key: "usu",     header: "Usuario" },
    // sin chips — este endpoint no los retorna
  ],

  "historial-completo-modem": [
    { key: "ot",        header: "OT" },
    { key: "req",       header: "REQ" },
    { key: "placa",     header: "Placa" },
    { key: "serie",     header: "Serie" },
    { key: "modelo",    header: "Modelo" },
    { key: "operacion", header: "Operación" },
    { key: "estado",    header: "Estado" },
    { key: "fecha",     header: "Fecha", render: (r) => formatDateDDMMYYYY(r?.fecha) },
    { key: "ciudad",    header: "Ciudad" },
    { key: "asignado",  header: "Asignado" },
    { key: "id_ref",    header: "Alias" },
    { key: "usu",       header: "Usuario" },
  ],
  
  "historial-completo-chip": [
    { key: "cod_ot",         header: "OT" },
    { key: "cod_req",    header: "REQ" },
    { key: "serie",      header: "Serie" },
    { key: "modelo",     header: "Modelo" },
    { key: "operacion",  header: "Operación" },
    { key: "estado",     header: "Estado" },
    { key: "fecha",      header: "Fecha", render: (r) => formatDateDDMMYYYY(r?.fecha) },
    { key: "ciudad",     header: "Ciudad" },
    { key: "relacion",   header: "Modem Relación" },
    { key: "precio",     header: "Precio" },
    { key: "usu",        header: "Usuario" },
  ],

  "historial-mantenimiento-modem": [
    { key: "req",         header: "REQ" },
    { key: "ot",     header: "OT" },
    { key: "placa",       header: "Placa" },
    { key: "serie",       header: "Serie" },
    { key: "modelo",      header: "Modelo" },
    { key: "operacion",   header: "Operación" },
    { key: "estado",      header: "Estado" },
    { key: "fecha",       header: "Fecha", render: (r) => formatDateDDMMYYYY(r?.fecha) },
    { key: "ciudad",      header: "Ciudad" },
    { key: "id_ref",      header: "Alias" },
    { key: "observacion", header: "Observación", cellSx: { maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis" } },
    { key: "usu",         header: "Usuario" },
  ],

  "detalle-ot": [
    { key: "id",               header: "OT" },
    { key: "nombre_tipo",      header: "Operación" },
    { key: "tipo",             header: "Tipo" },
    { key: "fecha",            header: "Fecha" },
    { key: "ciudad",           header: "Ciudad" },
    { key: "cant1",            header: "Cant. Modem" },
    { key: "ti1",              header: "Item 1" },
    { key: "cant2",            header: "Cant. Chips" },
    { key: "ti2",              header: "Item 2" },
    { key: "comision",         header: "Comisión" },
    { key: "obs",              header: "Observación", cellSx: { maxWidth: 250, overflow: "hidden", textOverflow: "ellipsis" } },
    { key: "usuario_vendedor", header: "Vendedor" },
  ],

  "detalle-req": [
    { key: "cod",              header: "REQ" },
    { key: "nombre_tipo",      header: "Operación" },
    { key: "estado",           header: "Estado" },
    { key: "ciudad",           header: "Ciudad" },
    { key: "fecha_proceso",    header: "Fecha Proceso",    render: (r) => formatDateDDMMYYYY(r?.fecha_proceso) },
    { key: "fecha_culminacion",header: "Fecha Culminación",render: (r) => formatDateDDMMYYYY(r?.fecha_culminacion) },
    { key: "cant1",            header: "Cant. Modem" },
    { key: "ti1",              header: "Item 1" },
    { key: "cant2",            header: "Cant. Chips" },
    { key: "ti2",              header: "Item 2" },
    { key: "es_terceria",      header: "Tercería", render: (r) => r?.es_terceria ? "Sí" : "No" },
    { key: "obs_comision",     header: "Obs. Comisión", cellSx: { maxWidth: 250, overflow: "hidden", textOverflow: "ellipsis" } },
    { key: "usuario_vendedor", header: "Vendedor" },
  ],

  "equipos-por-estado-modem": [
    { key: "placa",   header: "Placa" },
    { key: "id2",     header: "ID" },
    { key: "serie",   header: "Serie" },
    { key: "modelo",  header: "Modelo" },
    { key: "id_ref",  header: "Alias" },
    { key: "estado",  header: "Estado" },
    { key: "fecha",   header: "Fecha", render: (r) => formatDateDDMMYYYY(r?.fecha) },
    { key: "ciudad",  header: "Ciudad" },
    { key: "precio",  header: "Precio" },
    { key: "usu",     header: "Usuario" },
  ],

  "equipos-por-estado-chip": [
    { key: "placa",   header: "Placa" },
    { key: "id2",     header: "ID" },
    { key: "serie",   header: "Serie" },
    { key: "modelo",  header: "Modelo" },
    { key: "id_ref",  header: "Alias" },
    { key: "estado",  header: "Estado" },
    { key: "fecha",   header: "Fecha", render: (r) => formatDateDDMMYYYY(r?.fecha) },
    { key: "ciudad",  header: "Ciudad" },
    { key: "precio",  header: "Precio" },
  ],

  "equipos-por-estado-radio": [
    { key: "serie",   header: "Serie" },
    { key: "modelo",  header: "Modelo" },
    { key: "id_ref",  header: "Alias" },
    { key: "grupo",   header: "Grupo" },
    { key: "placa",   header: "Placa" },
    { key: "estado",  header: "Estado" },
    { key: "ciudad",  header: "Ciudad" },
    { key: "precio",  header: "Precio" },
    { key: "bateria_relacionada", header: "Batería relacionada" },
  ],

  "equipos-por-estado-bateria": [
    { key: "serie",    header: "Serie" },
    { key: "modelo",   header: "Modelo" },
    { key: "radio_relacionado", header: "Radio relacionado" },
    { key: "estado",   header: "Estado" },
    { key: "fecha",    header: "Fecha", render: (r) => formatDateDDMMYYYY(r?.fecha) },
    { key: "ciudad",   header: "Ciudad" },
    { key: "precio",   header: "Precio" },
    { key: "usu",      header: "Usuario" },
  ],

  "equipos-por-estado-varios": [
    { key: "serie",   header: "Serie" },
    { key: "emai",    header: "EMAI" },
    { key: "tipo",    header: "Tipo" },
    { key: "modelo",  header: "Modelo" },
    { key: "placa",   header: "Placa" },
    { key: "grupo",   header: "Grupo" },
    { key: "id_ref",  header: "Alias" },
    { key: "estado",  header: "Estado" },
    { key: "fecha",   header: "Fecha", render: (r) => formatDateDDMMYYYY(r?.fecha) },
    { key: "ciudad",  header: "Ciudad" },
    { key: "precio",  header: "Precio" },
    { key: "usu",     header: "Usuario" },
  ],

  // Igual que "equipos-por-estado-varios" pero con el chip y la batería
  // relacionados al radio (solo aplica al listado "Radios/Otros POC").
  "equipos-por-estado-varios-poc-otros": [
    { key: "serie",   header: "Serie" },
    { key: "emai",    header: "EMAI" },
    { key: "tipo",    header: "Tipo" },
    { key: "placa",   header: "Placa" },
    { key: "grupo",   header: "Grupo" },
    { key: "id_ref",  header: "Alias" },
    { key: "modelo",  header: "Modelo" },
    { key: "estado",  header: "Estado" },
    { key: "ciudad",  header: "Ciudad" },
    { key: "precio",  header: "Precio" },
    { key: "chip_relacionado",    header: "Chip relacionado" },
    { key: "bateria_relacionada", header: "Batería relacionada" },
  ],

  "equipos-por-estado-chip-poc": [
    { key: "serie",           header: "Serie" },
    { key: "emai",            header: "EMAI" },
    { key: "modelo",          header: "Modelo" },
    { key: "relacion",        header: "Relación" },
    { key: "equipo_relacion", header: "Equipo" },
    { key: "estado",          header: "Estado" },
    { key: "fecha",           header: "Fecha", render: (r) => formatDateDDMMYYYY(r?.fecha) },
    { key: "ciudad",          header: "Ciudad" },
    { key: "precio",          header: "Precio" },
    { key: "usu",             header: "Usuario" },
  ],

  "servicio-adicional": [
    { key: "cod_ot",      header: "OT" },
    { key: "serie",       header: "Serie" },
    { key: "nombre",      header: "Servicio" },
    { key: "operacion",   header: "Operación" },
    { key: "estado",      header: "Estado" },
    { key: "fecha",       header: "Fecha", render: (r) => formatDateDDMMYYYY(r?.fecha) },
    { key: "observacion", header: "Observación", cellSx: { maxWidth: 250, overflow: "hidden", textOverflow: "ellipsis" } },
    { key: "usu",         header: "Usuario" },
  ],

  "historial-mantenimiento-chip": [
    { key: "cod_req",    header: "REQ" },
    { key: "serie",      header: "Serie Chip" },
    { key: "relacion",   header: "Modem" },
    { key: "operacion",  header: "Operación" },
    { key: "estado",     header: "Estado" },
    { key: "fecha",      header: "Fecha", render: (r) => formatDateDDMMYYYY(r?.fecha) },
    { key: "ciudad",     header: "Ciudad" },
    { key: "observacion",header: "Observación", cellSx: { maxWidth: 250, overflow: "hidden", textOverflow: "ellipsis" } },
    { key: "usu",        header: "Usuario" },
  ],

  "historial-completo-radio": [
    { key: "ot",       header: "OT" },
    { key: "placa",    header: "Placa" },
    { key: "serie",    header: "Serie" },
    { key: "modelo",   header: "Modelo" },
    { key: "grupo",    header: "Grupo" },
    { key: "operacion",header: "Operación" },
    { key: "estado",   header: "Estado" },
    { key: "fecha",    header: "Fecha", render: (r) => formatDateDDMMYYYY(r?.fecha) },
    { key: "ciudad",   header: "Ciudad" },
    { key: "id_ref",   header: "Alias" },
    { key: "precio",   header: "Precio" },
    { key: "usu",      header: "Usuario" },
  ],

  "historial-completo-bateria": [
    { key: "ot",       header: "OT" },
    { key: "serie",    header: "Serie" },
    { key: "modelo",   header: "Modelo" },
    { key: "relacion", header: "Radio Relación" },
    { key: "operacion",header: "Operación" },
    { key: "estado",   header: "Estado" },
    { key: "fecha",    header: "Fecha", render: (r) => formatDateDDMMYYYY(r?.fecha) },
    { key: "ciudad",   header: "Ciudad" },
    { key: "precio",   header: "Precio" },
    { key: "usu",      header: "Usuario" },
  ],

  "accesorios-actuales": [
    { key: "modelo",   header: "Modelo" },
    { key: "ciudad",   header: "Ciudad" },
    {
      key: "cantidad",
      header: "Cantidad",
      render: (r) =>
        Number(r?.cantidad) < 0 ? (
          <span style={{ color: "#c62828", fontWeight: 700 }}>{r.cantidad}</span>
        ) : (
          r?.cantidad
        ),
    },
  ],

  "historial-completo-accesorio": [
    { key: "ot",       header: "OT" },
    { key: "modelo",   header: "Modelo" },
    { key: "cantidad", header: "Cantidad" },
    { key: "operacion",header: "Operación" },
    { key: "fecha",    header: "Fecha", render: (r) => formatDateDDMMYYYY(r?.fecha) },
    { key: "ciudad",   header: "Ciudad" },
    { key: "obs",      header: "Observación", cellSx: { maxWidth: 250, overflow: "hidden", textOverflow: "ellipsis" } },
    { key: "usu",      header: "Usuario" },
  ],

  "historial-mantenimiento-radio": [
    { key: "ot",       header: "OT" },
    { key: "serie",    header: "Serie" },
    { key: "modelo",   header: "Modelo" },
    { key: "operacion",header: "Operación" },
    { key: "estado",   header: "Estado" },
    { key: "fecha",    header: "Fecha", render: (r) => formatDateDDMMYYYY(r?.fecha) },
    { key: "ciudad",   header: "Ciudad" },
    { key: "obs",      header: "Observación", cellSx: { maxWidth: 250, overflow: "hidden", textOverflow: "ellipsis" } },
    { key: "precio",   header: "Precio" },
    { key: "usu",      header: "Usuario" },
  ],

  "historial-mantenimiento-bateria": [
    { key: "ot",       header: "OT" },
    { key: "serie",    header: "Serie" },
    { key: "modelo",   header: "Modelo" },
    { key: "operacion",header: "Operación" },
    { key: "estado",   header: "Estado" },
    { key: "fecha",    header: "Fecha", render: (r) => formatDateDDMMYYYY(r?.fecha) },
    { key: "ciudad",   header: "Ciudad" },
    { key: "obs",      header: "Observación", cellSx: { maxWidth: 250, overflow: "hidden", textOverflow: "ellipsis" } },
    { key: "precio",   header: "Precio" },
    { key: "usu",      header: "Usuario" },
  ],

  "historial-completo-varios": [
    { key: "ot",       header: "OT" },
    { key: "serie",    header: "Serie" },
    { key: "modelo",   header: "Modelo" },
    { key: "operacion",header: "Operación" },
    { key: "estado",   header: "Estado" },
    { key: "fecha",    header: "Fecha", render: (r) => formatDateDDMMYYYY(r?.fecha) },
    { key: "ciudad",   header: "Ciudad" },
    { key: "precio",   header: "Precio" },
    { key: "usu",      header: "Usuario" },
  ],

  "historial-mantenimiento-varios": [
    { key: "ot",       header: "OT" },
    { key: "serie",    header: "Serie" },
    { key: "modelo",   header: "Modelo" },
    { key: "operacion",header: "Operación" },
    { key: "estado",   header: "Estado" },
    { key: "fecha",    header: "Fecha", render: (r) => formatDateDDMMYYYY(r?.fecha) },
    { key: "ciudad",   header: "Ciudad" },
    { key: "precio",   header: "Precio" },
    { key: "usu",      header: "Usuario" },
  ],

  "historial-mantenimiento-poc-chip": [
    { key: "cod_ot",      header: "OT" },
    { key: "cod_req",     header: "REQ" },
    { key: "serie",       header: "Serie Chip" },
    { key: "relacion",    header: "Equipo" },
    { key: "operacion",   header: "Operación" },
    { key: "estado",      header: "Estado" },
    { key: "fecha",       header: "Fecha", render: (r) => formatDateDDMMYYYY(r?.fecha) },
    { key: "ciudad",      header: "Ciudad" },
    { key: "observacion", header: "Observación", cellSx: { maxWidth: 250, overflow: "hidden", textOverflow: "ellipsis" } },
    { key: "precio",      header: "Precio" },
    { key: "usu",         header: "Usuario" },
  ],

  // ── Agregar columnas de otras acciones aquí ──
  // "chips-activos": [ ... ],
  // "historial-modem": [ ... ],
};

// ─────────────────────────────────────────────────────────────
// CONFIGURACIÓN DE ACCIONES
// Para agregar un botón nuevo: solo agregar una entrada aquí.
// ─────────────────────────────────────────────────────────────
function buildActionsConfig(codServicio) {
  return {
    "modems-activos": {
      title: "Modem Activos y Vendidos Activos",
      searchPlaceholder: "Buscar placa / sim / imei / alias…",
      searchEnabled: true,
      columns: COLUMNS["modems-activos"],
      fetchFn: (params) => clienteServicioService.getModemsActivos(codServicio, params),
    },

    "modems-solo-activos": {
      title: "Modem Activos",
      searchPlaceholder: "Buscar placa / sim / imei / alias…",
      searchEnabled: true,
      columns: COLUMNS["modems-solo-activos"],
      fetchFn: (params) => clienteServicioService.getModemsSoloActivos(codServicio, params), // ← corregido
    },

    "historial-completo-modem": {
      title: "Historial Completo Modem",
      searchPlaceholder: "Buscar placa / serie…",
      searchEnabled: true,
      checkboxEnabled: false,
      editEnabled: false,
      columns: COLUMNS["historial-completo-modem"],
      fetchFn: (params) =>
        clienteServicioService.getHistorialCompletoEquipo(codServicio, {
          ...params,
          dato: 1,
        }),
    },
    
    "historial-completo-chip": {
      title: "Historial Completo Chips",
      searchPlaceholder: "Buscar serie / modelo…",
      searchEnabled: true,
      checkboxEnabled: false,
      editEnabled: false,
      columns: COLUMNS["historial-completo-chip"],
      fetchFn: (params) =>
        clienteServicioService.getHistorialCompletoEquipo(codServicio, {
          ...params,
          dato: 2,
        }),
    },

    "historial-mantenimiento-modem": {
      title: "Historial Mantenimiento Modem",
      searchPlaceholder: "Buscar placa / serie…",
      searchEnabled: true,
      checkboxEnabled: false,
      editEnabled: false,
      columns: COLUMNS["historial-mantenimiento-modem"],
      fetchFn: (params) => clienteServicioService.getHistorialMantenimientoModem(codServicio, params),
    },

    "detalle-ot": {
      title: "Detalle de OT",
      searchPlaceholder: "Buscar OT / operación…",
      searchEnabled: true,
      checkboxEnabled: false,
      editEnabled: false,
      columns: COLUMNS["detalle-ot"],
      fetchFn: (params) => clienteServicioService.getDetalleOT(codServicio, params),
    },

    "detalle-req": {
      title: "Detalle de Requerimiento",
      searchPlaceholder: "Buscar REQ / operación…",
      searchEnabled: true,
      checkboxEnabled: false,
      editEnabled: false,
      columns: COLUMNS["detalle-req"],
      fetchFn: (params) => clienteServicioService.getDetalleReq(codServicio, params),
    },

    "resumen": {
      title: "Resumen",
      isCustom: true,  // ← no usa HistorialResultsTable
      fetchFn: () => clienteServicioService.getResumenEquipo(codServicio),
    },

    // Reporte con sus propios filtros (rango de fechas del REQ): el componente consulta solo.
    "mantenimientos-por-req": {
      title: "Mantenimientos por requerimiento",
      isCustom: true,
    },

    "plataforma-wialon": {
      title: "Wialon",
      isCustom: true,  // ← no usa HistorialResultsTable, se renderiza aparte con ClientePlataformaVehiculos
      fetchFn: () => Promise.resolve(null),
    },

    "plataforma-leasecontrol": {
      title: "Leasecontrol",
      isCustom: true,
      fetchFn: () => Promise.resolve(null),
    },

    "plataforma-trsrastreo": {
      title: "TRS Rastreo",
      isCustom: true,
      fetchFn: () => Promise.resolve(null),
    },

    "modems-vendidos-activos": {
      title: "Modem Vendidos Activos",
      searchPlaceholder: "Buscar placa / serie / alias…",
      searchEnabled: true,
      checkboxEnabled: true,
      editEnabled: true,
      columns: COLUMNS["equipos-por-estado-modem"],
      fetchFn: (params) =>
        clienteServicioService.getEquiposPorEstado(codServicio, {
          ...params,
          estado: "vendido_activo",
          dato: 1,
        }),
    },
    
    "modems-vendidos-inactivos": {
      title: "Modem Vendidos Inactivos",
      searchPlaceholder: "Buscar placa / serie / alias…",
      searchEnabled: true,
      checkboxEnabled: true,
      editEnabled: true,
      columns: COLUMNS["equipos-por-estado-modem"],
      fetchFn: (params) =>
        clienteServicioService.getEquiposPorEstado(codServicio, {
          ...params,
          estado: "vendido",
          dato: 1,
        }),
    },

    "chips-activos": {
      title: "Chips Activos",
      searchPlaceholder: "Buscar placa / serie / alias…",
      searchEnabled: true,
      checkboxEnabled: true,
      editEnabled: true,
      columns: COLUMNS["equipos-por-estado-chip"],
      fetchFn: (params) =>
        clienteServicioService.getEquiposPorEstado(codServicio, {
          ...params,
          estado: "activo",
          dato: 2,
        }),
    },

    "chips-activos-y-vendidos": {
      title: "Chips Activas y Vendidas Activas",
      searchPlaceholder: "Buscar placa / serie / alias…",
      searchEnabled: true,
      checkboxEnabled: true,
      editEnabled: true,
      columns: COLUMNS["equipos-por-estado-chip"],
      fetchFn: (params) =>
        clienteServicioService.getEquiposPorEstado(codServicio, {
          ...params,
          estado: "activo_y_vendido_activo",
          dato: 2,
        }),
    },
    
    "historial-mantenimiento-chip": {
      title: "Historial Mantenimiento Chips",
      searchPlaceholder: "Buscar serie / modem…",
      searchEnabled: true,
      checkboxEnabled: false,
      editEnabled: false,
      columns: COLUMNS["historial-mantenimiento-chip"],
      fetchFn: (params) =>
        clienteServicioService.getMantenimientosEquipo(codServicio, {
          ...params,
          dato: 2,
        }),
    },

    "historial-completo-radio": {
      title: "Historial Completo Radio",
      searchPlaceholder: "Buscar placa / serie…",
      searchEnabled: true,
      checkboxEnabled: false,
      editEnabled: false,
      columns: COLUMNS["historial-completo-radio"],
      fetchFn: (params) =>
        clienteServicioService.getHistorialCompletoEquipo(codServicio, {
          ...params,
          dato: 1,
        }),
    },

    "historial-completo-bateria": {
      title: "Historial Completo Baterías",
      searchPlaceholder: "Buscar serie…",
      searchEnabled: true,
      checkboxEnabled: false,
      editEnabled: false,
      columns: COLUMNS["historial-completo-bateria"],
      fetchFn: (params) =>
        clienteServicioService.getHistorialCompletoEquipo(codServicio, {
          ...params,
          dato: 2,
        }),
    },

    "accesorios-actuales": {
      title: "Accesorios del Cliente",
      searchEnabled: false,
      checkboxEnabled: false,
      editEnabled: false,
      columns: COLUMNS["accesorios-actuales"],
      fetchFn: () => clienteServicioService.getAccesoriosActualesCliente(codServicio),
    },

    "historial-completo-accesorio": {
      title: "Historial Completo Accesorios",
      searchPlaceholder: "Buscar modelo…",
      searchEnabled: true,
      checkboxEnabled: false,
      editEnabled: false,
      columns: COLUMNS["historial-completo-accesorio"],
      fetchFn: (params) => clienteServicioService.getHistorialCompletoAccesorio(codServicio, params),
    },

    "historial-mantenimiento-radio": {
      title: "Historial Mantenimiento Radio",
      searchPlaceholder: "Buscar serie…",
      searchEnabled: true,
      checkboxEnabled: false,
      editEnabled: false,
      columns: COLUMNS["historial-mantenimiento-radio"],
      fetchFn: (params) =>
        clienteServicioService.getMantenimientosEquipo(codServicio, {
          ...params,
          dato: 1,
        }),
    },

    "historial-mantenimiento-bateria": {
      title: "Historial Mantenimiento Baterías",
      searchPlaceholder: "Buscar serie…",
      searchEnabled: true,
      checkboxEnabled: false,
      editEnabled: false,
      columns: COLUMNS["historial-mantenimiento-bateria"],
      fetchFn: (params) =>
        clienteServicioService.getMantenimientosEquipo(codServicio, {
          ...params,
          dato: 2,
        }),
    },

    "radios-activos": {
      title: "Radios Activos y Vendidos Activos",
      searchPlaceholder: "Buscar placa / serie / alias...",
      searchEnabled: true,
      checkboxEnabled: true,
      editEnabled: true,
      columns: COLUMNS["equipos-por-estado-radio"],
      fetchFn: (params) => clienteServicioService.getEquiposPorEstado(codServicio, { ...params, estado: "activo_y_vendido_activo", dato: 1 }),
    },

    "radios-vendidos-activos": {
      title: "Radios Vendidos Activos",
      searchPlaceholder: "Buscar placa / serie / alias...",
      searchEnabled: true,
      checkboxEnabled: true,
      editEnabled: true,
      columns: COLUMNS["equipos-por-estado-radio"],
      fetchFn: (params) => clienteServicioService.getEquiposPorEstado(codServicio, { ...params, estado: "vendido_activo", dato: 1 }),
    },

    "radios-vendidos-inactivos": {
      title: "Radios Vendidos Inactivos",
      searchPlaceholder: "Buscar placa / serie / alias...",
      searchEnabled: true,
      checkboxEnabled: true,
      editEnabled: true,
      columns: COLUMNS["equipos-por-estado-radio"],
      fetchFn: (params) => clienteServicioService.getEquiposPorEstado(codServicio, { ...params, estado: "vendido", dato: 1 }),
    },

    "baterias-activos": {
      title: "Baterias Activas",
      searchPlaceholder: "Buscar serie / modelo...",
      searchEnabled: true,
      checkboxEnabled: true,
      editEnabled: true,
      columns: COLUMNS["equipos-por-estado-bateria"],
      fetchFn: (params) => clienteServicioService.getEquiposPorEstado(codServicio, { ...params, estado: "activo", dato: 2 }),
    },

    "baterias-vendidos-activos": {
      title: "Baterias Vendidas Activas",
      searchPlaceholder: "Buscar serie / modelo...",
      searchEnabled: true,
      checkboxEnabled: true,
      editEnabled: true,
      columns: COLUMNS["equipos-por-estado-bateria"],
      fetchFn: (params) => clienteServicioService.getEquiposPorEstado(codServicio, { ...params, estado: "vendido_activo", dato: 2 }),
    },

    "baterias-vendidos-inactivos": {
      title: "Baterias Vendidas Inactivas",
      searchPlaceholder: "Buscar serie / modelo...",
      searchEnabled: true,
      checkboxEnabled: true,
      editEnabled: true,
      columns: COLUMNS["equipos-por-estado-bateria"],
      fetchFn: (params) => clienteServicioService.getEquiposPorEstado(codServicio, { ...params, estado: "vendido", dato: 2 }),
    },

    "varios-activos": {
      title: "Equipos Varios Activos y Vendidos",
      searchPlaceholder: "Buscar serie / modelo...",
      searchEnabled: true,
      checkboxEnabled: true,
      editEnabled: true,
      columns: COLUMNS["equipos-por-estado-varios"],
      // estado="todos": sin filtro (Activo, Mantenimiento, Vendido Activo y
      // Vendido, todos juntos) — a pedido para servicios que usan este grupo
      // (Energia, Seguridad, Ventas Varias: cualquier cod_servicio que no sea
      // Radio/Bateria, AVL o POC, ver getBotonesPorServicio).
      fetchFn: (params) => clienteServicioService.getEquiposPorEstado(codServicio, { ...params, estado: "todos", dato: 1 }),
    },

    "varios-vendidos-activos": {
      title: "Equipos Varios Vendidos Activos",
      searchPlaceholder: "Buscar serie / modelo...",
      searchEnabled: true,
      checkboxEnabled: true,
      editEnabled: true,
      columns: COLUMNS["equipos-por-estado-varios"],
      fetchFn: (params) => clienteServicioService.getEquiposPorEstado(codServicio, { ...params, estado: "vendido_activo", dato: 1 }),
    },

    "varios-vendidos-inactivos": {
      title: "Equipos Varios Vendidos Inactivos",
      searchPlaceholder: "Buscar serie / modelo...",
      searchEnabled: true,
      checkboxEnabled: true,
      editEnabled: true,
      columns: COLUMNS["equipos-por-estado-varios"],
      fetchFn: (params) => clienteServicioService.getEquiposPorEstado(codServicio, { ...params, estado: "vendido", dato: 1 }),
    },

    "historial-completo-varios": {
      title: "Historial Completo Varios",
      searchPlaceholder: "Buscar serie / modelo…",
      searchEnabled: true,
      checkboxEnabled: false,
      editEnabled: false,
      columns: COLUMNS["historial-completo-varios"],
      fetchFn: (params) =>
        clienteServicioService.getHistorialCompletoEquipo(codServicio, {
          ...params,
          dato: 1,
        }),
    },

    // POC: historial completo de Radios/Otros (equipos varios, dato 1) — igual que "Historial Completo Varios" pero con título propio
    "historial-completo-poc-varios": {
      title: "Historial Completo Radios/Otros POC",
      searchPlaceholder: "Buscar serie / modelo…",
      searchEnabled: true,
      checkboxEnabled: false,
      editEnabled: false,
      columns: COLUMNS["historial-completo-varios"],
      fetchFn: (params) =>
        clienteServicioService.getHistorialCompletoEquipo(codServicio, {
          ...params,
          dato: 1,
        }),
    },

    // POC: mantenimientos de Radios/Otros (equipos varios), Baterías y Chips (dato 1, 2 y 3)
    "historial-mantenimiento-poc-varios": {
      title: "Historial Mantenimiento Radios/Otros POC",
      searchPlaceholder: "Buscar serie / modelo…",
      searchEnabled: true,
      checkboxEnabled: false,
      editEnabled: false,
      columns: COLUMNS["historial-mantenimiento-varios"],
      fetchFn: (params) => clienteServicioService.getMantenimientosEquipo(codServicio, { ...params, dato: 1 }),
    },

    "historial-mantenimiento-poc-bateria": {
      title: "Historial Mantenimiento Baterías POC",
      searchPlaceholder: "Buscar serie…",
      searchEnabled: true,
      checkboxEnabled: false,
      editEnabled: false,
      columns: COLUMNS["historial-mantenimiento-bateria"],
      fetchFn: (params) => clienteServicioService.getMantenimientosEquipo(codServicio, { ...params, dato: 2 }),
    },

    "historial-mantenimiento-poc-chip": {
      title: "Historial Mantenimiento Chips POC",
      searchPlaceholder: "Buscar serie…",
      searchEnabled: true,
      checkboxEnabled: false,
      editEnabled: false,
      columns: COLUMNS["historial-mantenimiento-poc-chip"],
      fetchFn: (params) => clienteServicioService.getMantenimientosEquipo(codServicio, { ...params, dato: 3 }),
    },

    "historial-mantenimiento-varios": {
      title: "Historial Mantenimiento Varios",
      searchPlaceholder: "Buscar serie / modelo…",
      searchEnabled: true,
      checkboxEnabled: false,
      editEnabled: false,
      columns: COLUMNS["historial-mantenimiento-varios"],
      fetchFn: (params) =>
        clienteServicioService.getMantenimientosEquipo(codServicio, {
          ...params,
          dato: 1,
        }),
    },

    // "Baterias POC" y "Chips POC" ya no se dividen en Activos/Vendidos Activos/
    // Vendidos Inactivos — quedan en un solo botón cada uno (estado="todos": sin
    // filtro de estado, ver historialClienteService) porque en la práctica para
    // POC nunca existían resultados con el filtro anterior (buscaban un tipo
    // literal "Baterias" que no existe en los datos reales — confirmado en vivo).
    "poc-baterias-todos": {
      title: "Baterias POC",
      searchPlaceholder: "Buscar serie / emai / modelo...",
      searchEnabled: true,
      checkboxEnabled: true,
      editEnabled: true,
      columns: COLUMNS["equipos-por-estado-varios"],
      fetchFn: (params) => clienteServicioService.getEquiposPorEstado(codServicio, { ...params, estado: "todos", dato: 1 }),
    },

    "poc-chips-todos": {
      title: "Chips POC",
      searchPlaceholder: "Buscar chip / emai / relacion...",
      searchEnabled: true,
      checkboxEnabled: true,
      editEnabled: true,
      columns: COLUMNS["equipos-por-estado-chip-poc"],
      fetchFn: (params) => clienteServicioService.getEquiposPorEstado(codServicio, { ...params, estado: "todos", dato: 2 }),
    },

    // "Radios/Otros POC": las 3 de siempre (por estado) más un 4to botón con todo
    // junto (activos + vendidos activos + vendidos inactivos). Cada fila incluye
    // el chip y la batería relacionados al radio (columnas agregadas al final).
    "poc-varios-activos": {
      title: "Otros Equipos POC Activos",
      searchPlaceholder: "Buscar serie / emai / tipo / modelo...",
      searchEnabled: true,
      checkboxEnabled: true,
      editEnabled: true,
      columns: COLUMNS["equipos-por-estado-varios-poc-otros"],
      fetchFn: (params) => clienteServicioService.getEquiposPorEstado(codServicio, { ...params, estado: "activo", dato: 3 }),
    },

    "poc-varios-vendidos-activos": {
      title: "Otros Equipos POC Vendidos Activos",
      searchPlaceholder: "Buscar serie / emai / tipo / modelo...",
      searchEnabled: true,
      checkboxEnabled: true,
      editEnabled: true,
      columns: COLUMNS["equipos-por-estado-varios-poc-otros"],
      fetchFn: (params) => clienteServicioService.getEquiposPorEstado(codServicio, { ...params, estado: "vendido_activo", dato: 3 }),
    },

    "poc-varios-vendidos-inactivos": {
      title: "Otros Equipos POC Vendidos Inactivos",
      searchPlaceholder: "Buscar serie / emai / tipo / modelo...",
      searchEnabled: true,
      checkboxEnabled: true,
      editEnabled: true,
      columns: COLUMNS["equipos-por-estado-varios-poc-otros"],
      fetchFn: (params) => clienteServicioService.getEquiposPorEstado(codServicio, { ...params, estado: "vendido", dato: 3 }),
    },

    "poc-varios-todos": {
      title: "Radios/Otros POC",
      searchPlaceholder: "Buscar serie / emai / tipo / modelo...",
      searchEnabled: true,
      checkboxEnabled: true,
      editEnabled: true,
      columns: COLUMNS["equipos-por-estado-varios-poc-otros"],
      fetchFn: (params) => clienteServicioService.getEquiposPorEstado(codServicio, { ...params, estado: "todos", dato: 3 }),
    },

    // ── Agregar acciones nuevas aquí ──
    // "chips-activos": {
    //   title: "Chips Activas y Vendidas Activas",
    //   searchPlaceholder: "Buscar número / estado…",
    //   searchEnabled: true,
    //   columns: COLUMNS["chips-activos"],
    //   fetchFn: (params) => clienteServicioService.getChipsActivos(codServicio, params),
    // },
    // "historial-modem": {
    //   title: "Historial Completo Modem",
    //   searchEnabled: false,
    //   columns: COLUMNS["historial-modem"],
    //   fetchFn: (params) => clienteServicioService.getHistorialModem(codServicio, params),
    // },
  };
}

// ─────────────────────────────────────────────────────────────
// MAPEO: label del botón → clave de acción
// ─────────────────────────────────────────────────────────────
const BUTTON_ACTION_MAP = {
  "Modem Activos y Vendidos Activos": "modems-activos",
  "Modem Activos":                    "modems-solo-activos",
  "Historial Completo Modem":  "historial-completo-modem",
  "Historial Completo Chips":  "historial-completo-chip",
  "Historial Mantenimiento Modem": "historial-mantenimiento-modem",
  "Mantenimientos por REQ": "mantenimientos-por-req",
  "Detalle de OT": "detalle-ot",
  "Detalle de Requerimiento": "detalle-req",
  "Resumen": "resumen",
  "Wialon": "plataforma-wialon",
  "Leasecontrol": "plataforma-leasecontrol",
  "TRS Rastreo": "plataforma-trsrastreo",
  "Modem Vendidos Activos":   "modems-vendidos-activos",
  "Modem Vendidos Inactivos": "modems-vendidos-inactivos",
  "Chips Activos":           "chips-activos",
  "Historial Mantenimiento Chips": "historial-mantenimiento-chip",
  "Radios Activos y Vendidos Activos": "radios-activos",
  "Radios Vendidos Activos": "radios-vendidos-activos",
  "Radios Vendidos Inactivos": "radios-vendidos-inactivos",
  "Baterias Activas": "baterias-activos",
  "Baterias Vendidas Activas": "baterias-vendidos-activos",
  "Baterias Vendidas Inactivas": "baterias-vendidos-inactivos",
  "Historial Completo Radio": "historial-completo-radio",
  "Historial Mantenimiento Radio": "historial-mantenimiento-radio",
  "Historial Completo Baterias": "historial-completo-bateria",
  "Historial Mantenimiento Baterias": "historial-mantenimiento-bateria",
  "Equipos Varios Activos y Vendidos": "varios-activos",
  "Equipos Varios Vendidos Activos": "varios-vendidos-activos",
  "Equipos Varios Vendidos Inactivos": "varios-vendidos-inactivos",
  "Historial Completo Varios": "historial-completo-varios",
  "Historial Mantenimiento Varios": "historial-mantenimiento-varios",
  "Historial Mantenimiento Radios/Otros POC": "historial-mantenimiento-poc-varios",
  "Historial Completo Radios/Otros POC": "historial-completo-poc-varios",
  "Historial Mantenimiento Baterias POC":     "historial-mantenimiento-poc-bateria",
  "Historial Mantenimiento Chips POC":        "historial-mantenimiento-poc-chip",
  "Baterias POC": "poc-baterias-todos",
  "Chips POC": "poc-chips-todos",
  "Radios/Otros POC Activos": "poc-varios-activos",
  "Radios/Otros POC Vendidos Activos": "poc-varios-vendidos-activos",
  "Radios/Otros POC Vendidos Inactivos": "poc-varios-vendidos-inactivos",
  "Radios/Otros POC": "poc-varios-todos",
  "Accesorios del Cliente": "accesorios-actuales",
  "Historial Completo Accesorios": "historial-completo-accesorio",
  "Chips Activas y Vendidas Activas": "chips-activos-y-vendidos",
};

// Grupo de opciones excluyentes en una sola barra (control segmentado): la activa se "levanta" sobre el fondo.
function OpcionesSegmentadas({ etiqueta, Icono, opciones, activa, onSelect, color = "#00897b", suave = false }) {
  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0, maxWidth: "100%" }}>
      <Stack direction="row" alignItems="center" spacing={0.4} sx={{ color, flexShrink: 0 }}>
        {Icono && <Icono sx={{ fontSize: 16 }} />}
        <Typography sx={{ fontSize: "0.68rem", fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.4 }}>{etiqueta}</Typography>
      </Stack>
      <Box
        role="tablist"
        sx={{
          display: "flex", p: "3px", gap: "2px", borderRadius: 2, overflowX: "auto", minWidth: 0,
          bgcolor: alpha(color, 0.07),
          border: `1px solid ${alpha(color, 0.22)}`,
        }}
      >
        {opciones.map((o) => {
          const sel = activa === o.accion;
          return (
            <Box
              key={o.accion}
              component="button"
              type="button"
              role="tab"
              aria-selected={sel}
              onClick={() => onSelect(o.accion)}
              sx={{
                all: "unset", boxSizing: "border-box", cursor: "pointer", whiteSpace: "nowrap",
                px: 1.4, py: 0.55, borderRadius: 1.5, fontSize: "0.78rem", lineHeight: 1.4,
                fontWeight: sel ? 800 : 600,
                // Inventario: opción activa rellena con el color del tipo. Historial (suave): fondo claro con borde del color.
                color: sel ? (suave ? color : "#fff") : "text.secondary",
                bgcolor: sel ? (suave ? "background.paper" : color) : "transparent",
                boxShadow: sel ? `0 1px 3px ${alpha(color, 0.35)}${suave ? `, 0 0 0 1px ${alpha(color, 0.6)}` : ""}` : "none",
                transition: "background-color 120ms ease, color 120ms ease, box-shadow 120ms ease",
                "&:hover": sel ? {} : { color, bgcolor: alpha(color, 0.1) },
                "&:focus-visible": { outline: `2px solid ${color}`, outlineOffset: 1 },
              }}
            >
              {o.texto}
            </Box>
          );
        })}
      </Box>
    </Stack>
  );
}

// ─────────────────────────────────────────────────────────────
// MENÚ POR SERVICIO
// Primer nivel: tipo de equipo (o grupo de consultas). Segundo nivel: las consultas de ese tipo.
// `accion` es el label de BUTTON_ACTION_MAP; `texto` es lo que se muestra dentro de la pestaña.
// Las opciones con `historial: true` se muestran aparte, después de las de inventario.
// ─────────────────────────────────────────────────────────────
const accesoriosMenu = {
  clave: "accesorios", label: "Accesorios", Icono: ExtensionOutlinedIcon, color: "#1976d2", etiqueta: "Ver",
  opciones: [
    { accion: "Accesorios del Cliente", texto: "Actuales" },
    { accion: "Historial Completo Accesorios", texto: "Historial completo", historial: true },
  ],
};

const gestionMenu = (conRequerimientos = false) => ({
  clave: "gestion", label: "Gestión", Icono: AssignmentOutlinedIcon, color: "#7b5ea7", etiqueta: "Ver",
  opciones: [
    { accion: "Resumen", texto: "Resumen" },
    { accion: "Detalle de OT", texto: "Órdenes de trabajo" },
    ...(conRequerimientos ? [{ accion: "Detalle de Requerimiento", texto: "Requerimientos" }] : []),
  ],
});

const menuAvl = [
  {
    clave: "modem", label: "Modem", Icono: RouterOutlinedIcon, color: "#00897b",
    opciones: [
      { accion: "Modem Activos y Vendidos Activos", texto: "Activos y vendidos activos" },
      { accion: "Modem Activos", texto: "Solo activos" },
      { accion: "Modem Vendidos Activos", texto: "Vendidos activos" },
      { accion: "Modem Vendidos Inactivos", texto: "Vendidos inactivos" },
      { accion: "Historial Completo Modem", texto: "Historial completo", historial: true },
      { accion: "Historial Mantenimiento Modem", texto: "Mantenimiento", historial: true },
      { accion: "Mantenimientos por REQ", texto: "Mantenimiento por REQ (reporte)", historial: true },
    ],
  },
  {
    clave: "chips", label: "Chips", Icono: SimCardOutlinedIcon, color: "#e8710a",
    opciones: [
      { accion: "Chips Activas y Vendidas Activas", texto: "Activos y vendidos activos" },
      { accion: "Chips Activos", texto: "Solo activos" },
      { accion: "Historial Completo Chips", texto: "Historial completo", historial: true },
      { accion: "Historial Mantenimiento Chips", texto: "Mantenimiento", historial: true },
    ],
  },
  accesoriosMenu,
  {
    clave: "plataformas", label: "Plataformas", Icono: SatelliteAltOutlinedIcon, color: "#3f51b5", etiqueta: "Plataforma",
    opciones: [
      { accion: "Wialon", texto: "Wialon" },
      { accion: "Leasecontrol", texto: "LeaseControl" },
      { accion: "TRS Rastreo", texto: "TRS Rastreo" },
    ],
  },
  gestionMenu(true),
];

const menuRadio = [
  {
    clave: "radios", label: "Radios", Icono: SettingsInputAntennaOutlinedIcon, color: "#00897b",
    opciones: [
      { accion: "Radios Activos y Vendidos Activos", texto: "Activos y vendidos activos" },
      { accion: "Radios Vendidos Activos", texto: "Vendidos activos" },
      { accion: "Radios Vendidos Inactivos", texto: "Vendidos inactivos" },
      { accion: "Historial Completo Radio", texto: "Historial completo", historial: true },
      { accion: "Historial Mantenimiento Radio", texto: "Mantenimiento", historial: true },
    ],
  },
  {
    clave: "baterias", label: "Baterías", Icono: Battery5BarOutlinedIcon, color: "#c77700",
    opciones: [
      { accion: "Baterias Activas", texto: "Activas" },
      { accion: "Baterias Vendidas Activas", texto: "Vendidas activas" },
      { accion: "Baterias Vendidas Inactivas", texto: "Vendidas inactivas" },
      { accion: "Historial Completo Baterias", texto: "Historial completo", historial: true },
      { accion: "Historial Mantenimiento Baterias", texto: "Mantenimiento", historial: true },
    ],
  },
  accesoriosMenu,
  gestionMenu(),
];

const menuVarios = [
  {
    clave: "varios", label: "Equipos varios", Icono: DevicesOtherOutlinedIcon, color: "#00897b",
    opciones: [
      { accion: "Equipos Varios Activos y Vendidos", texto: "Activos y vendidos" },
      { accion: "Equipos Varios Vendidos Activos", texto: "Vendidos activos" },
      { accion: "Equipos Varios Vendidos Inactivos", texto: "Vendidos inactivos" },
      { accion: "Historial Completo Varios", texto: "Historial completo", historial: true },
      { accion: "Historial Mantenimiento Varios", texto: "Mantenimiento", historial: true },
    ],
  },
  accesoriosMenu,
  gestionMenu(),
];

const menuPoc = [
  {
    clave: "poc-radios", label: "Radios / Otros", Icono: SettingsInputAntennaOutlinedIcon, color: "#00897b",
    opciones: [
      { accion: "Radios/Otros POC", texto: "Todos" },
      { accion: "Radios/Otros POC Activos", texto: "Activos" },
      { accion: "Radios/Otros POC Vendidos Activos", texto: "Vendidos activos" },
      { accion: "Radios/Otros POC Vendidos Inactivos", texto: "Vendidos inactivos" },
      { accion: "Historial Completo Radios/Otros POC", texto: "Historial completo", historial: true },
      { accion: "Historial Mantenimiento Radios/Otros POC", texto: "Mantenimiento", historial: true },
    ],
  },
  {
    clave: "poc-baterias", label: "Baterías", Icono: Battery5BarOutlinedIcon, color: "#c77700",
    opciones: [
      { accion: "Baterias POC", texto: "Todas" },
      { accion: "Historial Mantenimiento Baterias POC", texto: "Mantenimiento", historial: true },
    ],
  },
  {
    clave: "poc-chips", label: "Chips", Icono: SimCardOutlinedIcon, color: "#e8710a",
    opciones: [
      { accion: "Chips POC", texto: "Todos" },
      { accion: "Historial Mantenimiento Chips POC", texto: "Mantenimiento", historial: true },
    ],
  },
  accesoriosMenu,
  gestionMenu(),
];

function getMenuPorServicio(codServicio) {
  const cod = Number(codServicio);
  if (cod === 1) return menuRadio;
  if (cod === 2 || cod === 3) return menuAvl;
  if (cod === 11) return menuPoc;
  return menuVarios;
}

// ─────────────────────────────────────────────────────────────
// ESTADO INICIAL DEL RESULTADO
// ─────────────────────────────────────────────────────────────
const RESULT_INITIAL = {
  title: "", subtitle: "", loading: false,
  error: null, columns: [], rows: [], pagination: null,
};

const PARAMS_INITIAL = { page: 1, limit: 50, q: "" };

// ─────────────────────────────────────────────────────────────
// COMPONENTE
// ─────────────────────────────────────────────────────────────
export default function HistorialCliente({ codServicioClienteProp, embedded = false, onBack }) {
  const { codServicioCliente: codFromParams } = useParams();
  const codServicioCliente = codServicioClienteProp ?? codFromParams;
  const navigate = useNavigate();

  const [clientData,     setClientData]     = useState(null);
  const [loadingCliente, setLoadingCliente] = useState(true);
  const [errorCliente,   setErrorCliente]   = useState(null);

  const [result,       setResult]       = useState(RESULT_INITIAL);
  const [activeAction, setActiveAction] = useState("modems-activos");

  // Un solo estado de parámetros — se resetea al cambiar de acción
  const [params, setParams] = useState(PARAMS_INITIAL);
  const [resumenData, setResumenData] = useState(null);
  const [editRow, setEditRow] = useState(null);
  const [editTipo, setEditTipo] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState("");
  const [centroEquipoDialog, setCentroEquipoDialog] = useState(null);
  // Click en el número de OT de la tabla "Detalle de OT" — abre la misma pantalla
  // que /orden-de-trabajo/ot/:id pero embebida en un Dialog (ver OrdenTrabajoDetalle,
  // prop idProp/embedded), sin salir de Historial de Cliente.
  const [otDialogId, setOtDialogId] = useState(null);

  const searchTimerRef = useRef(null);
  const prevActionRef  = useRef(null);

  // ── Config de acciones, se recalcula solo cuando cambia el cod ──
  const codServicioForApi = clientData?.cod_servicio_cliente;
  const menu = useMemo(() => getMenuPorServicio(clientData?.cod_servicio), [clientData?.cod_servicio]);
  const botonesVisibles = useMemo(
    () => menu.flatMap((g) => g.opciones.map((o) => ({ label: o.accion }))),
    [menu]
  );
  // Pestaña elegida a mano; si no hay, la que contiene la consulta activa.
  const [categoria, setCategoria] = useState(null);
  const grupoDeAccion = (accion) => menu.find((g) => g.opciones.some((o) => BUTTON_ACTION_MAP[o.accion] === accion))?.clave;
  const categoriaActiva = (categoria && menu.some((g) => g.clave === categoria) ? categoria : null)
    || grupoDeAccion(activeAction)
    || menu[0]?.clave;
  const grupoActivo = menu.find((g) => g.clave === categoriaActiva);

  const actionsConfig = useMemo(
    () => (codServicioForApi ? buildActionsConfig(codServicioForApi) : {}),
    [codServicioForApi]
  );

  useEffect(() => {
    if (!clientData) return;
    const validActions = botonesVisibles.map((btn) => BUTTON_ACTION_MAP[btn.label]).filter(Boolean);
    // null = el usuario ocultó la tabla ("Ocultar"): no se vuelve a abrir sola.
    if (activeAction === null || validActions.includes(activeAction)) return;
    setActiveAction(validActions[0] || null);
    setParams(PARAMS_INITIAL);
    setResult(RESULT_INITIAL);
    prevActionRef.current = null;
  }, [clientData, botonesVisibles, activeAction]);

  // ── Carga del cliente ──
  useEffect(() => {
    if (!codServicioCliente) return;
    const load = async () => {
      setLoadingCliente(true);
      setErrorCliente(null);
      try {
        const data = await clienteServicioService.getClienteServicio(codServicioCliente);
        setClientData(data);
      } catch {
        setErrorCliente("Error al cargar los datos del cliente.");
      } finally {
        setLoadingCliente(false);
      }
    };
    load();
  }, [codServicioCliente]);

  useEffect(() => {
    if (!activeAction || !codServicioForApi) return;
  
    const config = actionsConfig[activeAction];
    if (!config) return;
  
    // ── Rama custom (Resumen, etc.) ──
    if (config.isCustom) {
      if (!config.fetchFn) return; // vistas que cargan sus propios datos (p. ej. mantenimientos por REQ)
      const runCustom = async () => {
        setResult((prev) => ({ ...prev, loading: true, error: null, title: config.title }));
        try {
          const data = await config.fetchFn({});
          setResumenData(data);
          setResult((prev) => ({ ...prev, loading: false }));
        } catch {
          setResult((prev) => ({ ...prev, loading: false, error: "Error al cargar resumen." }));
          setResumenData(null);
        }
      };
      runCustom();
      return;
    }
  
    // ── Rama estándar ──
    const isNewAction = prevActionRef.current !== activeAction;
    prevActionRef.current = activeAction;
  
    const run = async (p) => {
      setResult((prev) => ({
        ...prev,
        title: config.title,
        subtitle: `cod_servicio: ${codServicioForApi} • página: ${p.page} • límite: ${p.limit}${p.q ? ` • filtro: ${p.q}` : ""}`,
        loading: true,
        error: null,
        columns: config.columns,
        rows: [],
      }));
      try {
        const data = await config.fetchFn(p);
        setResult((prev) => ({
          ...prev,
          loading: false,
          rows: Array.isArray(data?.items) ? data.items : [],
          pagination: data?.meta ?? null,
        }));
      } catch {
        setResult((prev) => ({ ...prev, loading: false, error: "Error al cargar los datos." }));
      }
    };
  
    if (isNewAction) {
      clearTimeout(searchTimerRef.current);
      run(params);
      return;
    }
  
    clearTimeout(searchTimerRef.current);
  
    if (params.q.trim().length > 0 && params.q.trim().length < 3) return;
  
    searchTimerRef.current = setTimeout(() => run(params), params.q ? 1200 : 0);
  
    return () => clearTimeout(searchTimerRef.current);
  }, [activeAction, params, codServicioForApi, actionsConfig]);

  // ── Handlers ──
  const seleccionarAccion = (label) => {
    const actionKey = BUTTON_ACTION_MAP[label];
    if (!actionKey || actionKey === activeAction) return;
    setParams(PARAMS_INITIAL);
    setResumenData(null);
    setActiveAction(actionKey);
  };

  // Al cambiar de pestaña se abre su primera consulta (la más usada de ese tipo).
  const seleccionarCategoria = (clave) => {
    setCategoria(clave);
    const grupo = menu.find((g) => g.clave === clave);
    if (grupo && grupoDeAccion(activeAction) !== clave) seleccionarAccion(grupo.opciones[0].accion);
  };

  const handlePageChange = (newPageZeroBased) =>
    setParams((p) => ({ ...p, page: newPageZeroBased + 1 }));

  const handleLimitChange = (newLimit) =>
    setParams((p) => ({ ...p, page: 1, limit: newLimit }));

  const handleSearchChange = (value) => {
    setResult((prev) => ({ ...prev, searchValue: value }));
    setParams((p) => ({ ...p, page: 1, q: value }));
  };

  const getCentroEquipoTipo = () => {
    if ([
      "modems-activos",
      "modems-solo-activos",
      "modems-vendidos-activos",
      "modems-vendidos-inactivos",
    ].includes(activeAction)) return "modem";

    if ([
      "chips-activos",
      "poc-chips-todos",
    ].includes(activeAction)) return "chip";

    if ([
      "radios-activos",
      "radios-vendidos-activos",
      "radios-vendidos-inactivos",
    ].includes(activeAction)) return "radio";

    if ([
      "baterias-activos",
      "baterias-vendidos-activos",
      "baterias-vendidos-inactivos",
    ].includes(activeAction)) return "bateria";

    if ([
      "varios-activos",
      "varios-vendidos-activos",
      "varios-vendidos-inactivos",
      "poc-baterias-todos",
      "poc-varios-activos",
      "poc-varios-vendidos-activos",
      "poc-varios-vendidos-inactivos",
      "poc-varios-todos",
    ].includes(activeAction)) return "varios";

    return null;
  };

  const getCentroEquipoIdentifier = (row, col) => {
    const tipo = getCentroEquipoTipo();
    if (!tipo || !row || !col?.key) return "";

    if (tipo === "modem") {
      if (col.key === "id" || col.key === "id2") return row?.[col.key] || row?.id || row?.id2 || "";
      if (col.key === "num_sim" || col.key === "serie") return row?.num_sim || row?.serie || "";
      if (col.key === "emai") return row?.emai || "";
      return "";
    }

    if (tipo === "chip") {
      return col.key === "serie" ? row?.serie || row?.numero || "" : "";
    }

    return col.key === "serie" ? row?.serie || row?.id2 || "" : "";
  };

  const isCentroEquipoCellClickable = (row, col) => {
    // "Detalle de OT" — el número de OT abre su propia pantalla (ver otDialogId
    // más abajo), no la de Centro de Equipos.
    if (activeAction === "detalle-ot" && col.key === "id") return !!row?.id;
    return !!getCentroEquipoIdentifier(row, col);
  };

  const handleCentroEquipoCellClick = (row, col) => {
    if (activeAction === "detalle-ot" && col.key === "id") {
      if (row?.id) setOtDialogId(row.id);
      return;
    }
    const tipo = getCentroEquipoTipo();
    const identificador = getCentroEquipoIdentifier(row, col);
    if (!tipo || !identificador) return;
    setCentroEquipoDialog({
      tipo,
      identificador: String(identificador),
      key: `${tipo}-${identificador}-${Date.now()}`,
    });
  };

  const isAvlModemEditAction =
    Number(clientData?.cod_servicio) === 2 &&
    ["modems-activos", "modems-solo-activos", "modems-vendidos-activos"].includes(activeAction);

  const isRadioEditAction = [
    "radios-activos",
    "radios-vendidos-activos",
  ].includes(activeAction);

  const isVariosEditAction = [
    "varios-activos",
    "varios-vendidos-activos",
    "poc-baterias-todos",
    "poc-varios-activos",
    "poc-varios-vendidos-activos",
    "poc-varios-todos",
  ].includes(activeAction);

  const editableTipo = isAvlModemEditAction
    ? "modem"
    : isRadioEditAction
      ? "radio"
      : isVariosEditAction
        ? "varios"
        : null;

  const handleEditRow = (row) => {
    if (!editableTipo) return;
    setEditError("");
    setEditTipo(editableTipo);
    setEditRow(row);
  };

  const handleSaveModemDatos = async (payload) => {
    const numSim = getNumSimFromRow(editRow);
    if (!numSim || !codServicioForApi) return;
    setEditLoading(true);
    setEditError("");
    try {
      const response = await clienteServicioService.updateModemDatosAvl(codServicioForApi, numSim, payload);
      const updated = response?.data || response;
      setResult((prev) => ({
        ...prev,
        rows: prev.rows.map((row) => {
          if (getNumSimFromRow(row) !== numSim) return row;
          return {
            ...row,
            id_ref: updated.id_ref ?? payload.id_ref,
            grupo: updated.grupo ?? payload.grupo,
            transportista: updated.transportista ?? payload.transportista,
            sociedad: updated.sociedad ?? payload.sociedad,
            bandera: updated.bandera ?? payload.bandera,
            sensores: updated.sensores ?? payload.sensores,
          };
        }),
      }));
      setEditRow(null);
      setEditTipo(null);
    } catch (error) {
      setEditError(error?.response?.data?.message || error?.response?.data?.error || "No se pudo actualizar el modem.");
    } finally {
      setEditLoading(false);
    }
  };

  const handleSaveEquipoDatos = async (payload) => {
    const serie = editRow?.serie || editRow?.id2;
    if (!serie || !codServicioForApi || !editTipo) return;
    setEditLoading(true);
    setEditError("");
    try {
      const response = await clienteServicioService.updateEquipoDatos(codServicioForApi, editTipo, serie, payload);
      const updated = response?.data || response;
      setResult((prev) => ({
        ...prev,
        rows: prev.rows.map((row) => {
          const rowSerie = row?.serie || row?.id2;
          if (rowSerie !== serie) return row;
          return {
            ...row,
            placa: updated.placa ?? payload.placa,
            grupo: updated.grupo ?? payload.grupo,
            id_ref: updated.id_ref ?? payload.id_ref,
          };
        }),
      }));
      setEditRow(null);
      setEditTipo(null);
    } catch (error) {
      setEditError(error?.response?.data?.message || error?.response?.data?.error || "No se pudo actualizar el equipo.");
    } finally {
      setEditLoading(false);
    }
  };

  const handleClear = () => {
    clearTimeout(searchTimerRef.current);
    setResult(RESULT_INITIAL);
    setActiveAction(null);
    setParams(PARAMS_INITIAL);
    prevActionRef.current = null;
    setResumenData(null);
  };

  const handleBack = () => {
    if (typeof onBack === "function") return onBack();
    if (!embedded) navigate(-1);
  };

  // ── Guards de carga/error del cliente ──
  if (loadingCliente) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "50vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (errorCliente) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{errorCliente}</Alert>
        <Button variant="contained" onClick={handleBack} sx={{ mt: 2 }}>Volver</Button>
      </Box>
    );
  }

  const currentConfig = activeAction ? actionsConfig[activeAction] : null;

  // ── Ficha del cliente ──
  const estadoActivo = /activ/i.test(String(clientData?.estado || "")) && !/inactiv/i.test(String(clientData?.estado || ""));
  const datosCliente = [
    { label: "Descripción",        value: clientData?.descripcion },
    { label: "Sucursal",           value: clientData?.sucursal_facturar },
    { label: "Forma de pago",      value: clientData?.pago ? getPagoLabel(clientData.pago) : "" },
    { label: "Moneda",             value: MONEDAS[clientData?.moneda] },
    { label: "Ejecutivo de venta", value: clientData?.ejecutivo_venta },
    { label: "Fecha registro",     value: clientData?.fecha_registro ? formatDateDDMMYYYY(clientData.fecha_registro) : "" },
    { label: "Teléfono",           value: clientData?.telefono_cliente },
    ...(clientData?.central_riesgo && clientData.central_riesgo !== "No"
      ? [{ label: "Central de riesgo", value: clientData.central_riesgo, alerta: true }]
      : []),
  ];
  const opcionesInventario = grupoActivo?.opciones.filter((o) => !o.historial) || [];
  const opcionesHistorial = grupoActivo?.opciones.filter((o) => o.historial) || [];
  const accionActivaLabel = grupoActivo?.opciones.find((o) => BUTTON_ACTION_MAP[o.accion] === activeAction)?.accion || null;

  const content = (
    <Box sx={{ padding: embedded ? 0 : 3 }}>
      {!embedded && (
        <Stack direction="row" alignItems="center" spacing={1}
          sx={{ borderBottom: (theme) => `1px solid ${theme.palette.divider}`, pb: 2, mb: 3 }}>
          <IconButton aria-label="Volver atrás" onClick={handleBack} color="default">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" component="h1" fontWeight={700} sx={{ fontSize: "1.05rem" }}>
            {clientData?.nombre_cliente || "Detalle del Cliente"}
          </Typography>
        </Stack>
      )}

      {/* Ficha del cliente */}
      <Paper variant="outlined" sx={{ mb: 1.5, borderRadius: 1.5, overflow: "hidden" }}>
        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap
          sx={{ px: 1.5, py: 1, borderBottom: (theme) => `1px solid ${theme.palette.divider}`, bgcolor: (theme) => alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.12 : 0.04) }}>
          {clientData?.nombre_servicio && (
            <Chip size="small" color="primary" label={clientData.nombre_servicio} sx={{ fontWeight: 800 }} />
          )}
          {clientData?.estado && (
            <Chip size="small" variant="outlined" color={estadoActivo ? "success" : "default"} label={clientData.estado}
              icon={<Box component="span" sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: estadoActivo ? "success.main" : "text.disabled", ml: "8px !important" }} />}
              sx={{ fontWeight: 700 }} />
          )}
          {clientData?.tipo && <Chip size="small" variant="outlined" label={clientData.tipo} sx={{ fontWeight: 600 }} />}
          <Typography sx={{ ml: "auto !important", fontSize: "0.75rem", color: "text.secondary" }}>
            Código de servicio <Box component="b" sx={{ color: "text.primary", fontVariantNumeric: "tabular-nums" }}>{clientData?.cod_servicio_cliente || "—"}</Box>
          </Typography>
        </Stack>
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))", columnGap: 2, rowGap: 1, px: 1.5, py: 1.1 }}>
          {datosCliente.map(({ label, value, alerta }) => (
            <Box key={label} sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: "0.66rem", color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.3, fontWeight: 700 }} noWrap>
                {label}
              </Typography>
              <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: alerta ? "error.main" : value ? "text.primary" : "text.disabled" }} noWrap title={value || ""}>
                {value || "—"}
              </Typography>
            </Box>
          ))}
          {clientData?.observacion && (
            <Box sx={{ gridColumn: "1 / -1", minWidth: 0 }}>
              <Typography sx={{ fontSize: "0.66rem", color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.3, fontWeight: 700 }}>Observación</Typography>
              <Typography sx={{ fontSize: "0.8rem", whiteSpace: "pre-wrap" }}>{clientData.observacion}</Typography>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Consultas: primero el tipo (pestaña), después la consulta de ese tipo */}
      <Paper variant="outlined" sx={{ mb: 1, borderRadius: 1.5, overflow: "hidden" }}>
        <Tabs
          value={categoriaActiva || false}
          onChange={(_, v) => seleccionarCategoria(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: 46,
            px: 0.5,
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
            bgcolor: (theme) => alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.08 : 0.03),
            "& .MuiTabs-indicator": { height: 3, borderRadius: "3px 3px 0 0", bgcolor: grupoActivo?.color },
            "& .MuiTab-root": {
              minHeight: 46, textTransform: "none", fontWeight: 700, fontSize: "0.84rem", px: 2, color: "text.secondary",
              "&:hover": { color: "text.primary", bgcolor: (theme) => alpha(theme.palette.primary.main, 0.05) },
            },
            "& .MuiTab-root.Mui-selected": { fontWeight: 800, bgcolor: "background.paper" },
          }}
        >
          {menu.map(({ clave, label, Icono, color }) => {
            const sel = clave === categoriaActiva;
            return (
              <Tab
                key={clave}
                value={clave}
                label={label}
                iconPosition="start"
                icon={(
                  <Box component="span" sx={{
                    width: 28, height: 28, borderRadius: "50%", display: "inline-flex", alignItems: "center", justifyContent: "center",
                    color: sel ? "#fff" : color, bgcolor: sel ? color : alpha(color, 0.12), transition: "all 150ms ease",
                  }}>
                    <Icono sx={{ fontSize: 17 }} />
                  </Box>
                )}
                sx={{ "&.Mui-selected": { color }, "&:hover .MuiBox-root": { bgcolor: sel ? color : alpha(color, 0.22) } }}
              />
            );
          })}
        </Tabs>
        <Stack direction="row" alignItems="center" flexWrap="wrap" useFlexGap columnGap={3} rowGap={1}
          sx={{ px: 1.5, py: 1.1, bgcolor: grupoActivo?.color ? alpha(grupoActivo.color, 0.035) : "transparent", transition: "background-color 150ms ease" }}>
          {opcionesInventario.length > 0 && (
            <OpcionesSegmentadas etiqueta={grupoActivo?.etiqueta || "Estado"} opciones={opcionesInventario} activa={accionActivaLabel} onSelect={seleccionarAccion} color={grupoActivo?.color} />
          )}
          {opcionesHistorial.length > 0 && (
            <OpcionesSegmentadas etiqueta="Historial" Icono={HistoryIcon} opciones={opcionesHistorial} activa={accionActivaLabel} onSelect={seleccionarAccion} color={grupoActivo?.color} suave />
          )}
        </Stack>
      </Paper>

      {result.loading && !result.title ? null : (
  <>
    {/* Tabla estándar */}
    {!currentConfig?.isCustom &&
      (result.title || result.loading || result.error || result.rows.length > 0) && (
        <HistorialResultsTable
          title={result.title}
          subtitle={result.subtitle}
          loading={result.loading}
          error={result.error}
          columns={result.columns}
          rows={result.rows}
          tipo={activeAction || ""}
          onClear={handleClear}
          pagination={result.pagination}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleLimitChange}
          rowsPerPageOptions={[10, 25, 50, 100, 200]}
          searchEnabled={currentConfig?.searchEnabled ?? false}
          searchValue={params.q}
          onSearchChange={handleSearchChange}
          searchPlaceholder={currentConfig?.searchPlaceholder ?? "Buscar..."}
          checkboxEnabled={currentConfig?.checkboxEnabled ?? true}
          editEnabled={(currentConfig?.editEnabled ?? true) && !!editableTipo}
          onEdit={handleEditRow}
          isCellClickable={isCentroEquipoCellClickable}
          onCellClick={handleCentroEquipoCellClick}
          getRowSx={
            ["historial-completo-modem", "historial-completo-chip", "historial-mantenimiento-modem"]
              .includes(activeAction)
              ? getRowSxByEstado
              : null
          }
        />
    )}

    {/* Resumen custom */}
    {currentConfig?.isCustom && activeAction === "resumen" && (resumenData || result.loading || result.error) && (
      result.loading
        ? <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}><CircularProgress size={20} /></Box>
        : result.error
          ? <Alert severity="error" sx={{ mt: 1 }}>{result.error}</Alert>
          : <ResumenEquipo data={resumenData} onClear={handleClear} />
    )}

    {activeAction === "mantenimientos-por-req" && codServicioForApi && (
      <MantenimientosPorReq codServicio={codServicioForApi} nombreCliente={clientData?.nombre_cliente} />
    )}

    {/* Plataforma de rastreo (Wialon/Leasecontrol/TRS Rastreo) — vista simple de solo lectura.
        Un cliente puede estar vinculado a mas de una a la vez (se emparejo en
        momentos distintos), por eso son 3 botones separados en vez de uno solo
        que "adivina" cual mostrar. */}
    {activeAction?.startsWith("plataforma-") && codServicioForApi && (
      <ClientePlataformaVehiculos
        codServicioCliente={codServicioForApi}
        plataforma={activeAction.replace("plataforma-", "")}
      />
    )}

    <Dialog
      open={!!otDialogId}
      onClose={() => setOtDialogId(null)}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 1.25, maxHeight: "92vh" } }}
    >
      <DialogTitle sx={{ py: 1, fontSize: "0.95rem", fontWeight: 900, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        Orden de Trabajo
        <IconButton size="small" onClick={() => setOtDialogId(null)}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0, bgcolor: "background.default" }}>
        {otDialogId && <OrdenTrabajoDetalle idProp={otDialogId} embedded />}
      </DialogContent>
    </Dialog>

    <Dialog
      open={!!centroEquipoDialog}
      onClose={() => setCentroEquipoDialog(null)}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 1.25,
          maxHeight: "92vh",
        },
      }}
    >
      <DialogTitle sx={{ py: 1, fontSize: "0.95rem", fontWeight: 900 }}>
        Centro de equipos
        <Typography variant="caption" sx={{ display: "block", color: "text.secondary" }}>
          {centroEquipoDialog?.tipo?.toUpperCase()} - {centroEquipoDialog?.identificador}
        </Typography>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 1, bgcolor: "background.default" }}>
        {centroEquipoDialog && (
          <BusquedaPorIdentificador
            key={centroEquipoDialog.key}
            initialTipo={centroEquipoDialog.tipo}
            initialBuscar={centroEquipoDialog.identificador}
            autoLoadKey={centroEquipoDialog.key}
            hideSearch
          />
        )}
      </DialogContent>
      <DialogActions sx={{ p: 0.75 }}>
        <Button size="small" onClick={() => setCentroEquipoDialog(null)}>
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>

    <ModemDatosAvlDialog
      open={!!editRow && editTipo === "modem"}
      row={editRow}
      loading={editLoading}
      error={editError}
      onClose={() => {
        if (!editLoading) {
          setEditRow(null);
          setEditTipo(null);
        }
      }}
      onSave={handleSaveModemDatos}
    />
    <EquipoDatosDialog
      open={!!editRow && (editTipo === "radio" || editTipo === "varios")}
      tipo={editTipo}
      row={editRow}
      loading={editLoading}
      error={editError}
      onClose={() => {
        if (!editLoading) {
          setEditRow(null);
          setEditTipo(null);
        }
      }}
      onSave={handleSaveEquipoDatos}
    />
  </>
)}
    </Box>
  );

  return embedded ? content : <DashboardLayout>{content}</DashboardLayout>;
}

